/**
 * The commission plan an affiliate's deal names (TMMRSL-0050, TMMAPI-0104). An agreement names a
 * plan instead of a rate (the generated `AffiliateAgreementView.commissionPlan`, a
 * `CommissionPlanRef`); each referral names the plan in force when it was attributed
 * (`AffiliateReferralRow.commissionPlanId`), and the date it was first paid (`firstPaidOn`, absent
 * until paid). The one place these wire fields are read, through the generated types; the deal is
 * written as the generated `AffiliateAgreementWriteRequest`.
 */
import type { CommissionPlanStatus } from '@/api/commissionPlans';
import { backendCode } from '@/api/errors';
import type {
  AffiliateAgreementView,
  AffiliateReferralRow,
  CommissionPlanRef,
} from '@/api/generated';

/** The plan an agreement names, with its own status (the deal's chip and warning read it). */
export interface PlanRef {
  id: number;
  name: string;
  status: CommissionPlanStatus;
  noncompliant: boolean;
}

const STATUSES: readonly CommissionPlanStatus[] = ['DRAFT', 'ACTIVE', 'CLOSED'];

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * The plan an agreement names (`commissionPlan`, never null since TMMAPI-0125); `null` only when
 * there is no agreement.
 */
export function planRefOf(agreement: AffiliateAgreementView): PlanRef;
export function planRefOf(agreement: AffiliateAgreementView | null | undefined): PlanRef | null;
export function planRefOf(agreement: AffiliateAgreementView | null | undefined): PlanRef | null {
  if (!agreement) return null;
  const ref: CommissionPlanRef = agreement.commissionPlan;
  return {
    id: ref.id,
    name: ref.name,
    // An unknown status can't be given again, as `commissionPlans.ts` reads it.
    status: STATUSES.find((status) => status === ref.status) ?? 'CLOSED',
    noncompliant: ref.noncompliant,
  };
}

/** The plan a referral was attributed under (`commissionPlanId`); `null` when it names none. */
export function referralPlanIdOf(referral: unknown): number | null {
  if (!isObject(referral)) return null;
  const id: unknown = (referral as Partial<AffiliateReferralRow>).commissionPlanId;
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

/** A referral's first paid date (`YYYY-MM-DD`); `null` when unpaid, absent or malformed. */
export function firstPaidOnOf(referral: unknown): string | null {
  if (!isObject(referral)) return null;
  const value: unknown = (referral as Partial<AffiliateReferralRow>).firstPaidOn;
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

/**
 * Approving answered that the affiliate's deal names no plan: `409 COMMISSION_PLAN_REQUIRED`
 * (TMMAPI-0104), or the `AGREEMENT_REQUIRED` it replaces.
 */
export function isPlanRequired(err: unknown): boolean {
  const code = backendCode(err);
  return code === 'COMMISSION_PLAN_REQUIRED' || code === 'AGREEMENT_REQUIRED';
}

